import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, Trash2, GripVertical, ChevronDown, ChevronRight,
  Settings, Eye, Copy, Check, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChecklistTemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const isNew = templateId === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [expandedSections, setExpandedSections] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  
  // Drag state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [dragType, setDragType] = useState(null); // 'section' or 'criteria'
  
  // Template state
  const [template, setTemplate] = useState({
    name: '',
    description: '',
    version: '1.0',
    stage: 3,
    tenant_id: null,
    sections: [],
    is_active: true,
    is_default: false
  });

  // New section/criteria dialogs
  const [newSectionOpen, setNewSectionOpen] = useState(false);
  const [newCriteriaOpen, setNewCriteriaOpen] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [editingCriteria, setEditingCriteria] = useState(null);
  
  const [newSection, setNewSection] = useState({
    id: '',
    title: '',
    title_en: '',
    description: ''
  });
  
  const [newCriteria, setNewCriteria] = useState({
    id: '',
    title: '',
    description: '',
    questions: [{ tr: '', en: '' }],
    required: true
  });

  const loadTemplate = useCallback(async () => {
    if (isNew) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/checklist-templates/${templateId}`, { withCredentials: true });
      setTemplate(response.data);
      
      // Expand all sections by default
      const expanded = {};
      response.data.sections?.forEach((_, idx) => {
        expanded[idx] = true;
      });
      setExpandedSections(expanded);
    } catch (error) {
      console.error('Failed to load template:', error);
      toast.error('Şablon yüklenemedi');
      navigate('/checklist-templates');
    } finally {
      setLoading(false);
    }
  }, [templateId, isNew, navigate]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleSave = async () => {
    if (!template.name.trim()) {
      toast.error('Şablon adı zorunludur');
      return;
    }
    
    try {
      setSaving(true);
      
      if (isNew) {
        await axios.post(`${API}/checklist-templates`, template, { withCredentials: true });
        toast.success('Şablon oluşturuldu');
      } else {
        await axios.put(`${API}/checklist-templates/${templateId}`, template, { withCredentials: true });
        toast.success('Şablon güncellendi');
      }
      
      navigate('/checklist-templates');
    } catch (error) {
      toast.error('Şablon kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  // Section drag handlers
  const handleSectionDragStart = (e, index) => {
    setDraggedItem(index);
    setDragType('section');
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionDragOver = (e, index) => {
    e.preventDefault();
    if (dragType !== 'section') return;
    setDragOverItem(index);
  };

  const handleSectionDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragType !== 'section' || draggedItem === null) return;
    
    const newSections = [...template.sections];
    const [removed] = newSections.splice(draggedItem, 1);
    newSections.splice(dropIndex, 0, removed);
    
    setTemplate({ ...template, sections: newSections });
    setDraggedItem(null);
    setDragOverItem(null);
    setDragType(null);
  };

  // Criteria drag handlers
  const handleCriteriaDragStart = (e, sectionIndex, criteriaIndex) => {
    setDraggedItem({ sectionIndex, criteriaIndex });
    setDragType('criteria');
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCriteriaDragOver = (e, sectionIndex, criteriaIndex) => {
    e.preventDefault();
    if (dragType !== 'criteria') return;
    setDragOverItem({ sectionIndex, criteriaIndex });
  };

  const handleCriteriaDrop = (e, dropSectionIndex, dropCriteriaIndex) => {
    e.preventDefault();
    if (dragType !== 'criteria' || !draggedItem) return;
    
    const newSections = [...template.sections];
    const sourceSection = newSections[draggedItem.sectionIndex];
    const [removed] = sourceSection.criteria.splice(draggedItem.criteriaIndex, 1);
    
    const targetSection = newSections[dropSectionIndex];
    targetSection.criteria.splice(dropCriteriaIndex, 0, removed);
    
    setTemplate({ ...template, sections: newSections });
    setDraggedItem(null);
    setDragOverItem(null);
    setDragType(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
    setDragType(null);
  };

  // Add section
  const handleAddSection = () => {
    const section = {
      id: newSection.id || `section_${template.sections.length + 1}`,
      title: newSection.title,
      title_en: newSection.title_en,
      description: newSection.description,
      criteria: [],
      order: template.sections.length
    };
    
    setTemplate({
      ...template,
      sections: [...template.sections, section]
    });
    
    setExpandedSections({ ...expandedSections, [template.sections.length]: true });
    setNewSectionOpen(false);
    setNewSection({ id: '', title: '', title_en: '', description: '' });
    toast.success('Bölüm eklendi');
  };

  // Delete section
  const handleDeleteSection = (index) => {
    const newSections = template.sections.filter((_, i) => i !== index);
    setTemplate({ ...template, sections: newSections });
    toast.success('Bölüm silindi');
  };

  // Add criteria
  const handleAddCriteria = () => {
    if (editingSectionIndex === null) return;
    
    const criteria = {
      id: newCriteria.id,
      title: newCriteria.title,
      description: newCriteria.description,
      questions: newCriteria.questions.filter(q => q.tr || q.en),
      required: newCriteria.required,
      order: template.sections[editingSectionIndex].criteria.length
    };
    
    const newSections = [...template.sections];
    newSections[editingSectionIndex].criteria.push(criteria);
    
    setTemplate({ ...template, sections: newSections });
    setNewCriteriaOpen(false);
    setNewCriteria({
      id: '',
      title: '',
      description: '',
      questions: [{ tr: '', en: '' }],
      required: true
    });
    toast.success('Kriter eklendi');
  };

  // Delete criteria
  const handleDeleteCriteria = (sectionIndex, criteriaIndex) => {
    const newSections = [...template.sections];
    newSections[sectionIndex].criteria.splice(criteriaIndex, 1);
    setTemplate({ ...template, sections: newSections });
    toast.success('Kriter silindi');
  };

  // Add question to criteria form
  const addQuestion = () => {
    setNewCriteria({
      ...newCriteria,
      questions: [...newCriteria.questions, { tr: '', en: '' }]
    });
  };

  // Update question
  const updateQuestion = (index, field, value) => {
    const newQuestions = [...newCriteria.questions];
    newQuestions[index][field] = value;
    setNewCriteria({ ...newCriteria, questions: newQuestions });
  };

  // Remove question
  const removeQuestion = (index) => {
    const newQuestions = newCriteria.questions.filter((_, i) => i !== index);
    setNewCriteria({ ...newCriteria, questions: newQuestions });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const totalCriteria = template.sections.reduce((acc, s) => acc + (s.criteria?.length || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/checklist-templates')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {isNew ? 'Yeni Şablon Oluştur' : template.name}
                </h1>
                <p className="text-sm text-gray-500">
                  {template.sections.length} bölüm · {totalCriteria} kriter
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setPreviewOpen(true)}>
                <Eye className="w-4 h-4 mr-2" />
                Önizle
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="w-4 h-4" />
              Ayarlar
            </TabsTrigger>
            <TabsTrigger value="sections" className="gap-2">
              <GripVertical className="w-4 h-4" />
              Bölümler ve Kriterler
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="bg-white rounded-lg border p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Şablon Adı *</Label>
                  <Input
                    id="name"
                    value={template.name}
                    onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                    placeholder="örn: GSTC V.3.0 Hotels"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="version">Versiyon</Label>
                  <Input
                    id="version"
                    value={template.version}
                    onChange={(e) => setTemplate({ ...template, version: e.target.value })}
                    placeholder="örn: 1.0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  value={template.description || ''}
                  onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                  placeholder="Şablon hakkında açıklama..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Denetim Aşaması</Label>
                  <Select 
                    value={template.stage?.toString()} 
                    onValueChange={(v) => setTemplate({ ...template, stage: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Aşama seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2. Aşama (TR-I)</SelectItem>
                      <SelectItem value="3">3. Aşama (GSTC)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Tenant</Label>
                  <Select 
                    value={template.tenant_id || 'all'} 
                    onValueChange={(v) => setTemplate({ ...template, tenant_id: v === 'all' ? null : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tenant seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Tenantlar</SelectItem>
                      <SelectItem value="tenant_royalcert_default">RoyalCert</SelectItem>
                      <SelectItem value="tenant_trb_default">TRB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-8 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={template.is_active}
                    onCheckedChange={(v) => setTemplate({ ...template, is_active: v })}
                  />
                  <Label>Aktif</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={template.is_default}
                    onCheckedChange={(v) => setTemplate({ ...template, is_default: v })}
                  />
                  <Label>Varsayılan Şablon</Label>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Sections Tab */}
          <TabsContent value="sections">
            <div className="space-y-4">
              {/* Add Section Button */}
              <Button 
                onClick={() => setNewSectionOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              >
                <Plus className="w-4 h-4" />
                Bölüm Ekle
              </Button>

              {/* Sections List */}
              {template.sections.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
                  <p className="text-gray-500">Henüz bölüm eklenmemiş. "Bölüm Ekle" butonuna tıklayarak başlayın.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {template.sections.map((section, sectionIndex) => (
                    <div
                      key={section.id || sectionIndex}
                      draggable
                      onDragStart={(e) => handleSectionDragStart(e, sectionIndex)}
                      onDragOver={(e) => handleSectionDragOver(e, sectionIndex)}
                      onDrop={(e) => handleSectionDrop(e, sectionIndex)}
                      onDragEnd={handleDragEnd}
                      className={`bg-white rounded-lg border transition-all ${
                        dragOverItem === sectionIndex && dragType === 'section'
                          ? 'border-emerald-500 ring-2 ring-emerald-200'
                          : 'border-gray-200'
                      }`}
                    >
                      <Collapsible
                        open={expandedSections[sectionIndex]}
                        onOpenChange={(open) => 
                          setExpandedSections({ ...expandedSections, [sectionIndex]: open })
                        }
                      >
                        <div className="flex items-center gap-3 p-4">
                          <div className="cursor-grab text-gray-400 hover:text-gray-600">
                            <GripVertical className="w-5 h-5" />
                          </div>
                          
                          <CollapsibleTrigger className="flex-1 flex items-center gap-2 text-left">
                            {expandedSections[sectionIndex] ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                            <div>
                              <div className="font-medium text-gray-900">{section.title}</div>
                              <div className="text-sm text-gray-500">
                                {section.criteria?.length || 0} kriter
                              </div>
                            </div>
                          </CollapsibleTrigger>

                          <Badge variant="outline">{section.id}</Badge>
                          
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteSection(sectionIndex)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        <CollapsibleContent>
                          <div className="px-4 pb-4 border-t">
                            <div className="pt-4 space-y-2">
                              {section.criteria?.map((criteria, criteriaIndex) => (
                                <div
                                  key={criteria.id || criteriaIndex}
                                  draggable
                                  onDragStart={(e) => handleCriteriaDragStart(e, sectionIndex, criteriaIndex)}
                                  onDragOver={(e) => handleCriteriaDragOver(e, sectionIndex, criteriaIndex)}
                                  onDrop={(e) => handleCriteriaDrop(e, sectionIndex, criteriaIndex)}
                                  onDragEnd={handleDragEnd}
                                  className={`flex items-center gap-3 p-3 bg-gray-50 rounded-lg border transition-all ${
                                    dragOverItem?.sectionIndex === sectionIndex && 
                                    dragOverItem?.criteriaIndex === criteriaIndex && 
                                    dragType === 'criteria'
                                      ? 'border-emerald-500 ring-2 ring-emerald-200'
                                      : 'border-transparent hover:border-gray-200'
                                  }`}
                                >
                                  <div className="cursor-grab text-gray-400 hover:text-gray-600">
                                    <GripVertical className="w-4 h-4" />
                                  </div>
                                  <Badge variant="outline" className="text-xs">{criteria.id}</Badge>
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{criteria.title}</p>
                                    {criteria.questions?.length > 0 && (
                                      <p className="text-xs text-gray-500">{criteria.questions.length} soru</p>
                                    )}
                                  </div>
                                  {criteria.required ? (
                                    <Badge className="bg-amber-100 text-amber-700 text-xs">Zorunlu</Badge>
                                  ) : (
                                    <Badge className="bg-gray-100 text-gray-700 text-xs">Opsiyonel</Badge>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeleteCriteria(sectionIndex, criteriaIndex)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}

                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full mt-2 gap-2"
                                onClick={() => {
                                  setEditingSectionIndex(sectionIndex);
                                  setNewCriteriaOpen(true);
                                }}
                              >
                                <Plus className="w-4 h-4" />
                                Kriter Ekle
                              </Button>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* New Section Dialog */}
      <Dialog open={newSectionOpen} onOpenChange={setNewSectionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Bölüm Ekle</DialogTitle>
            <DialogDescription>
              Kontrol listesine yeni bir bölüm ekleyin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Bölüm ID</Label>
              <Input
                value={newSection.id}
                onChange={(e) => setNewSection({ ...newSection, id: e.target.value })}
                placeholder="örn: section_a"
              />
            </div>
            <div className="space-y-2">
              <Label>Başlık (Türkçe) *</Label>
              <Input
                value={newSection.title}
                onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
                placeholder="örn: Bölüm A: Sürdürülebilir Yönetim"
              />
            </div>
            <div className="space-y-2">
              <Label>Başlık (İngilizce)</Label>
              <Input
                value={newSection.title_en}
                onChange={(e) => setNewSection({ ...newSection, title_en: e.target.value })}
                placeholder="örn: Section A: Sustainable Management"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewSectionOpen(false)}>İptal</Button>
            <Button onClick={handleAddSection} disabled={!newSection.title.trim()}>
              Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Criteria Dialog */}
      <Dialog open={newCriteriaOpen} onOpenChange={setNewCriteriaOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yeni Kriter Ekle</DialogTitle>
            <DialogDescription>
              Bölüme yeni bir kriter ekleyin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kriter ID *</Label>
                <Input
                  value={newCriteria.id}
                  onChange={(e) => setNewCriteria({ ...newCriteria, id: e.target.value })}
                  placeholder="örn: A1"
                />
              </div>
              <div className="space-y-2 flex items-end">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newCriteria.required}
                    onCheckedChange={(v) => setNewCriteria({ ...newCriteria, required: v })}
                  />
                  <Label>Zorunlu Kriter</Label>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Başlık *</Label>
              <Input
                value={newCriteria.title}
                onChange={(e) => setNewCriteria({ ...newCriteria, title: e.target.value })}
                placeholder="örn: Sustainability Management System / Sürdürülebilirlik Yönetim Sistemi"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={newCriteria.description}
                onChange={(e) => setNewCriteria({ ...newCriteria, description: e.target.value })}
                placeholder="Kriter açıklaması..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Sorular (TR/EN)</Label>
                <Button variant="outline" size="sm" onClick={addQuestion}>
                  <Plus className="w-4 h-4 mr-1" />
                  Soru Ekle
                </Button>
              </div>
              
              <div className="space-y-3">
                {newCriteria.questions.map((q, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500">Soru {idx + 1}</span>
                      {newCriteria.questions.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeQuestion(idx)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <Input
                      value={q.tr}
                      onChange={(e) => updateQuestion(idx, 'tr', e.target.value)}
                      placeholder="Türkçe soru..."
                    />
                    <Input
                      value={q.en}
                      onChange={(e) => updateQuestion(idx, 'en', e.target.value)}
                      placeholder="English question..."
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCriteriaOpen(false)}>İptal</Button>
            <Button 
              onClick={handleAddCriteria} 
              disabled={!newCriteria.id.trim() || !newCriteria.title.trim()}
            >
              Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Şablon Önizleme</DialogTitle>
            <DialogDescription>
              {template.name} - {template.sections.length} bölüm, {totalCriteria} kriter
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {template.sections.map((section, sIdx) => (
              <div key={sIdx} className="border rounded-lg overflow-hidden">
                <div className="bg-emerald-50 px-4 py-3 font-medium text-emerald-900">
                  {section.title}
                </div>
                <div className="divide-y">
                  {section.criteria?.map((criteria, cIdx) => (
                    <div key={cIdx} className="p-4">
                      <div className="flex items-start gap-3">
                        <Badge variant="outline">{criteria.id}</Badge>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{criteria.title}</p>
                          {criteria.description && (
                            <p className="text-sm text-gray-500 mt-1">{criteria.description}</p>
                          )}
                          {criteria.questions?.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {criteria.questions.map((q, qIdx) => (
                                <li key={qIdx} className="text-sm text-gray-600">
                                  • {q.tr || q.en}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {criteria.required && (
                          <Badge className="bg-amber-100 text-amber-700">Zorunlu</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setPreviewOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
